'use client';

/**
 * ContactSupportSection - Client component for the FAQ page contact section
 * Provides a button to open the contact support dialog
 */
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import type { AskQueryFormValues } from '@ui/components/features/landing/forms/AskQueryForm';
import { ContactSupportDialog } from '@ui/components/features/support';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { BrowserApiClient } from '@website/lib/api/browser';
import { collectClientContext } from '@website/utils/collectClientContext';

export default function ContactSupportSection() {
  const t = useTranslations('FAQ');
  const tSupport = useTranslations('ContactSupport');
  const { data: user } = useCurrentUser();
  const [showSupportDialog, setShowSupportDialog] = useState(false);

  const handleSubmitSupport = async (data: AskQueryFormValues, attachments?: File[], bookingId?: string) => {
    let attachmentKeys: string[] = [];

    // Upload attachments if present
    if (attachments && attachments.length > 0) {
      for (const file of attachments) {
        const uploadResponse = await BrowserApiClient.getPrivateAssetUploadUrl({
          filename: file.name,
          type: file.type
        });

        const uploadResult = await fetch(uploadResponse.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type }
        });

        if (!uploadResult.ok) {
          throw new Error('Failed to upload attachment');
        }

        attachmentKeys.push(uploadResponse.key);
      }
    }

    // Send the query (email is optional for logged-in users)
    await BrowserApiClient.sendQuery({
      ...(data.email && { email: data.email }),
      message: data.question,
      attachments: attachmentKeys,
      bookingId,
      clientContext: collectClientContext()
    });
  };

  return (
    <>
      <div className="mt-8 pt-6 border-t border-neutral-200">
        <h3 className="text-base font-medium text-neutral-900 mb-3">{t('stillHaveQuestions')}</h3>
        <p className="text-neutral-700">
          {t('contactSupportDescription')}{' '}
          <button
            type="button"
            onClick={() => setShowSupportDialog(true)}
            className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
          >
            {t('contactSupportLink')}
          </button>
        </p>
      </div>

      <ContactSupportDialog
        isOpen={showSupportDialog}
        onClose={() => setShowSupportDialog(false)}
        defaultEmail={user?.email}
        onSubmit={handleSubmitSupport}
        translations={{
          title: tSupport('title'),
          email: tSupport('email'),
          emailPlaceholder: tSupport('emailPlaceholder'),
          question: tSupport('question'),
          questionPlaceholder: tSupport('questionPlaceholder'),
          attachment: tSupport('attachment'),
          attachmentPrompt: tSupport('attachmentPrompt'),
          submitButton: tSupport('submitButton'),
          submittingButton: tSupport('submittingButton'),
          responseTime: tSupport('responseTime'),
          successMessage: tSupport('successMessage'),
          successDescription: tSupport('successDescription'),
          errorMessage: tSupport('errorMessage'),
          emailError: tSupport('emailError'),
          questionError: tSupport('questionError')
        }}
      />
    </>
  );
}
